import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import {
  AutomationOutreachSplitWithTreatments,
  Treatment,
} from "@redotech/redo-merchant-marketing-rpc/schema/automation-outreach-splits/get-automation-outreach-split";
import { AutomationOutreachStepType } from "@redotech/redo-storefront-widget-common/split";
import { toast } from "@redotech/redo-web/alert";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

export interface UseAbTestEditorOptions {
  automationId: string | undefined;
  stepId: string;
  stepType: AutomationOutreachStepType;
  splitId: string | undefined;
  existingTemplateId: string | undefined;
  existingTemplateName: string | undefined;
  onSplitIdChange: (splitId: string) => void;
  onSave: (() => void) | undefined;
  createTemplate: (name: string) => Promise<string>;
  getEditPath: (templateId: string, returnPath: string) => string;
  getViewPath?: (templateId: string, returnPath: string) => string;
}

export type AbTestView = "loading" | "results" | "editor" | "inspector";

export interface UseAbTestEditorResult {
  view: AbTestView;
  splitWithTreatments: AutomationOutreachSplitWithTreatments | null;
  refetch: () => void;
  openEditor: () => void;
  closeEditor: () => void;
  editVariant: (treatment: Treatment) => void;
  viewVariant: (treatment: Treatment) => void;
  /** Variant creation modal state */
  variantModal: {
    isOpen: boolean;
    variantName: string;
    templateName: string;
    setTemplateName: (name: string) => void;
    open: (variantName: string, defaultTemplateName: string) => void;
    close: () => void;
    create: () => Promise<void>;
  };
}

export function useAbTestEditor({
  automationId,
  stepId,
  stepType,
  splitId,
  existingTemplateId,
  existingTemplateName,
  onSplitIdChange,
  onSave,
  createTemplate,
  getEditPath,
  getViewPath,
}: UseAbTestEditorOptions): UseAbTestEditorResult {
  const { t } = useTranslation();
  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [refetchKey, refetch] = useReducer((x) => x + 1, 0);

  // Check if we should auto-open from URL param (returning from variant editing)
  const shouldOpenFromUrl = searchParams.get("openAbTestEditor") === "true";
  const [isEditorOpen, setIsEditorOpen] = useState(shouldOpenFromUrl);

  // Clean up URL param after reading it
  useEffect(() => {
    if (shouldOpenFromUrl) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("openAbTestEditor");
      setSearchParams(newParams, { replace: true });
    }
  }, [shouldOpenFromUrl, searchParams, setSearchParams]);

  // Variant modal state
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [variantName, setVariantName] = useState("");
  const [variantTemplateName, setVariantTemplateName] = useState("");

  // Load split with treatments
  const splitWithTreatmentsLoad = useLoad(async () => {
    if (!marketingRpcClient || !automationId || !stepId) {
      return null;
    }
    try {
      return await marketingRpcClient.getAutomationOutreachSplit({
        automationId,
        automationStepId: stepId,
      });
    } catch {
      return null;
    }
  }, [marketingRpcClient, automationId, stepId, refetchKey]);

  const splitWithTreatments = splitWithTreatmentsLoad.value ?? null;
  const isSplitActive = splitWithTreatments?.split.active ?? false;

  // Determine which view to show
  const view = useMemo<AbTestView>(() => {
    if (splitWithTreatmentsLoad.pending) {
      return "loading";
    }
    if (isSplitActive) {
      return "results";
    }
    if (isEditorOpen) {
      return "editor";
    }
    return "inspector";
  }, [splitWithTreatmentsLoad.pending, isSplitActive, isEditorOpen]);

  const buildReturnPath = useCallback(
    () =>
      `returnPath=${encodeURIComponent(location.pathname)}&selected=${stepId}&openAbTestEditor=true`,
    [location.pathname, stepId],
  );

  // Open editor - creates split if needed
  const openEditor = useHandler(async () => {
    if (!automationId || !existingTemplateId) {
      setIsEditorOpen(true);
      return;
    }

    // If split already exists, just open editor
    if (splitWithTreatments) {
      setIsEditorOpen(true);
      return;
    }

    // Create new split with existing template as first treatment
    try {
      const defaultTestName = existingTemplateName
        ? `${existingTemplateName} A/B Test`
        : "Untitled A/B Test";

      const result = await marketingRpcClient.createAutomationOutreachSplit({
        automationId,
        automationStepId: stepId,
        stepType,
        name: defaultTestName,
        treatments: [
          {
            percentage: 100,
            isControl: false,
            templateId: existingTemplateId,
            name: existingTemplateName || "Variant A",
          },
        ],
      });

      onSplitIdChange(result.split._id);
      onSave?.();
      refetch();
      setIsEditorOpen(true);
    } catch (e) {
      console.error("Failed to create A/B test split", e);
      toast(t("Failed to create A/B test"), { variant: "error" });
    }
  });

  const closeEditor = useCallback(() => {
    setIsEditorOpen(false);
    refetch();
  }, [refetch]);

  const editVariant = useCallback(
    (treatment: Treatment) => {
      if (!treatment.templateId) {
        return;
      }
      onSave?.();
      navigate(getEditPath(treatment.templateId, buildReturnPath()));
    },
    [navigate, getEditPath, buildReturnPath, onSave],
  );

  const viewVariant = useCallback(
    (treatment: Treatment) => {
      if (!treatment.templateId || !getViewPath) {
        return;
      }
      navigate(getViewPath(treatment.templateId, buildReturnPath()));
    },
    [navigate, getViewPath, buildReturnPath],
  );

  // Variant modal handlers
  const openVariantModal = useCallback(
    (name: string, defaultTemplateName: string) => {
      setVariantName(name);
      setVariantTemplateName(defaultTemplateName);
      setVariantModalOpen(true);
    },
    [],
  );

  const closeVariantModal = useCallback(() => {
    setVariantModalOpen(false);
    setVariantName("");
    setVariantTemplateName("");
  }, []);

  const createVariant = useHandler(async () => {
    const currentSplitId = splitWithTreatments?.split._id ?? splitId;
    if (!currentSplitId || !automationId) {
      return;
    }

    try {
      // Create the template
      const newTemplateId = await createTemplate(variantTemplateName);

      // Fetch current treatments
      const splitData = await marketingRpcClient.getAutomationOutreachSplit({
        automationId,
        automationStepId: stepId,
      });

      if (!splitData) {
        throw new Error("Failed to fetch A/B test data");
      }

      // Add new treatment
      const updatedTreatments = [
        ...splitData.treatments.map((t) => ({
          percentage: t.percentage,
          isControl: t.isControl,
          templateId: t.templateId,
          name: t.templateName,
        })),
        {
          percentage: 0,
          isControl: false,
          templateId: newTemplateId,
          name: variantTemplateName,
        },
      ];

      await marketingRpcClient.updateAutomationOutreachSplit({
        splitId: currentSplitId,
        treatments: updatedTreatments,
      });

      closeVariantModal();
      onSplitIdChange(currentSplitId);
      onSave?.();

      // Navigate to template editor
      navigate(getEditPath(newTemplateId, buildReturnPath()));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t("Failed to create template");
      toast(message, { variant: "error" });
    }
  });

  return {
    view,
    splitWithTreatments,
    refetch,
    openEditor,
    closeEditor,
    editVariant,
    viewVariant,
    variantModal: {
      isOpen: variantModalOpen,
      variantName,
      templateName: variantTemplateName,
      setTemplateName: setVariantTemplateName,
      open: openVariantModal,
      close: closeVariantModal,
      create: createVariant,
    },
  };
}
